import { useEffect, useState } from 'react';
import type {
  ComponentTemplate,
  MechanicalMounting,
  MechanicalOrientation,
  Pin,
  PinKind,
  PinSide,
} from '../types';

const PIN_KINDS: PinKind[] = ['power', 'gnd', 'signal', 'comm', 'analog', 'pwm', 'other'];
const PIN_SIDES: PinSide[] = ['left', 'right', 'top', 'bottom'];
const MOUNTING_OPTIONS: MechanicalMounting[] = [
  'free',
  'din-rail',
  'panel',
  'door',
  'chassis',
  'bracket',
  'other',
];
const ORIENTATION_OPTIONS: Array<{ value: MechanicalOrientation; label: string }> = [
  { value: 'top', label: 'Top view (W x H)' },
  { value: 'side', label: 'Side mount (D x H)' },
  { value: 'front', label: 'Front mount (W x D)' },
];

interface Props {
  template: ComponentTemplate | null;
  onSave: (t: ComponentTemplate) => void;
  onCancel: () => void;
}

export function ComponentEditor({ template, onSave, onCancel }: Props) {
  const [draft, setDraft] = useState<ComponentTemplate | null>(template);
  const [connectorDraft, setConnectorDraft] = useState<{
    name: string;
    count: number;
    side: PinSide;
    kind: PinKind;
  } | null>(null);

  useEffect(() => setDraft(template), [template]);

  if (!draft) return null;

  const update = (patch: Partial<ComponentTemplate>) => setDraft({ ...draft, ...patch });
  const updateMechanical = (patch: Partial<NonNullable<ComponentTemplate['mechanical']>>) =>
    update({ mechanical: { ...(draft.mechanical ?? {}), ...patch } });

  const updatePin = (index: number, patch: Partial<Omit<Pin, 'id'>>) => {
    const pins = draft.pins.map((p, i) => (i === index ? { ...p, ...patch } : p));
    update({ pins });
  };

  const removePin = (index: number) =>
    update({ pins: draft.pins.filter((_, i) => i !== index) });

  const addPin = () =>
    update({
      pins: [
        ...draft.pins,
        { name: `P${draft.pins.length + 1}`, side: 'left', kind: 'other' },
      ],
    });

  const addGroup = () => {
    setConnectorDraft({ name: '', count: 4, side: 'bottom', kind: 'signal' });
  };

  const commitConnector = () => {
    if (!connectorDraft) return;
    const name = connectorDraft.name.trim();
    const count = Math.max(1, Math.min(100, Math.round(connectorDraft.count) || 1));
    const prefix = name ? name.slice(0, 3).toUpperCase().replace(/\s+/g, '') : 'P';
    const newPins: Omit<Pin, 'id'>[] = Array.from({ length: count }, (_, i) => ({
      name: `${prefix}${i + 1}`,
      side: connectorDraft.side,
      kind: connectorDraft.kind,
      group: name || undefined,
    }));
    update({ pins: [...draft.pins, ...newPins] });
    setConnectorDraft(null);
  };

  const movePin = (index: number, dir: -1 | 1) => {
    const next = [...draft.pins];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    update({ pins: next });
  };

  const addProperty = () => {
    let i = Object.keys(draft.defaultProperties).length + 1;
    let key = `prop${i}`;
    while (key in draft.defaultProperties) {
      i += 1;
      key = `prop${i}`;
    }
    update({ defaultProperties: { ...draft.defaultProperties, [key]: '' } });
  };

  const setPropertyKey = (oldKey: string, newKey: string) => {
    if (oldKey === newKey) return;
    const entries = Object.entries(draft.defaultProperties);
    const mapped: Record<string, string> = {};
    for (const [k, v] of entries) mapped[k === oldKey ? newKey : k] = v;
    update({ defaultProperties: mapped });
  };

  const setPropertyValue = (key: string, value: string) =>
    update({ defaultProperties: { ...draft.defaultProperties, [key]: value } });

  const removeProperty = (key: string) => {
    const copy = { ...draft.defaultProperties };
    delete copy[key];
    update({ defaultProperties: copy });
  };

  return (
    <div style={overlayStyle} onClick={onCancel}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>
            {template?.key && draft.key === template.key ? 'Edit component' : 'New component'}
          </div>
          <button style={iconBtn} onClick={onCancel}>
            ×
          </button>
        </div>

        <div style={{ display: 'flex', gap: 16, padding: 16, overflow: 'auto' }}>
          <div style={{ width: 280, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <SectionTitle>BASICS</SectionTitle>
            <Field label="Label">
              <input value={draft.label} onChange={(e) => update({ label: e.target.value })} style={input} />
            </Field>
            <Field label="Category">
              <input value={draft.category} onChange={(e) => update({ category: e.target.value })} style={input} />
            </Field>
            <Field label="Part number">
              <input
                value={draft.partNumber ?? ''}
                onChange={(e) => update({ partNumber: e.target.value })}
                style={input}
              />
            </Field>
            <Field label="Manufacturer">
              <input
                value={draft.manufacturer ?? ''}
                onChange={(e) => update({ manufacturer: e.target.value })}
                style={input}
              />
            </Field>
            <Field label="Color">
              <input
                type="color"
                value={draft.color ?? '#334155'}
                onChange={(e) => update({ color: e.target.value })}
                style={{ ...input, padding: 0, height: 30 }}
              />
            </Field>

            <SectionTitle style={{ marginTop: 10 }}>MECHANICAL</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <Field label="Width (mm)">
                <input
                  type="number"
                  min={0}
                  step={0.1}
                  value={draft.mechanical?.widthMm ?? ''}
                  onChange={(e) =>
                    updateMechanical({
                      widthMm: e.target.value === '' ? undefined : Number(e.target.value),
                    })
                  }
                  style={input}
                />
              </Field>
              <Field label="Height (mm)">
                <input
                  type="number"
                  min={0}
                  step={0.1}
                  value={draft.mechanical?.heightMm ?? ''}
                  onChange={(e) =>
                    updateMechanical({
                      heightMm: e.target.value === '' ? undefined : Number(e.target.value),
                    })
                  }
                  style={input}
                />
              </Field>
              <Field label="Depth (mm)">
                <input
                  type="number"
                  min={0}
                  step={0.1}
                  value={draft.mechanical?.depthMm ?? ''}
                  onChange={(e) =>
                    updateMechanical({
                      depthMm: e.target.value === '' ? undefined : Number(e.target.value),
                    })
                  }
                  style={input}
                />
              </Field>
              <Field label="Mass (kg)">
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={draft.mechanical?.massKg ?? ''}
                  onChange={(e) =>
                    updateMechanical({
                      massKg: e.target.value === '' ? undefined : Number(e.target.value),
                    })
                  }
                  style={input}
                />
              </Field>
            </div>
            <Field label="Mounting">
              <select
                value={draft.mechanical?.mounting ?? 'free'}
                onChange={(e) =>
                  updateMechanical({ mounting: e.target.value as MechanicalMounting })
                }
                style={input}
              >
                {MOUNTING_OPTIONS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Default mount pose">
              <select
                value={draft.mechanical?.orientation ?? 'top'}
                onChange={(e) =>
                  updateMechanical({ orientation: e.target.value as MechanicalOrientation })
                }
                style={input}
              >
                {ORIENTATION_OPTIONS.map((orientation) => (
                  <option key={orientation.value} value={orientation.value}>
                    {orientation.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Clearance (mm)">
              <input
                type="number"
                min={0}
                step={0.1}
                value={draft.mechanical?.clearanceMm ?? ''}
                onChange={(e) =>
                  updateMechanical({
                    clearanceMm: e.target.value === '' ? undefined : Number(e.target.value),
                  })
                }
                style={input}
              />
            </Field>
            <Field label="Mechanical notes">
              <textarea
                rows={3}
                value={draft.mechanical?.notes ?? ''}
                onChange={(e) => updateMechanical({ notes: e.target.value })}
                style={{ ...input, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </Field>

            <SectionTitle style={{ marginTop: 10 }}>DEFAULT PROPERTIES</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {Object.entries(draft.defaultProperties).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', gap: 4 }}>
                  <input
                    value={k}
                    onChange={(e) => setPropertyKey(k, e.target.value)}
                    style={{ ...input, flex: 1 }}
                  />
                  <input
                    value={v}
                    onChange={(e) => setPropertyValue(k, e.target.value)}
                    style={{ ...input, flex: 1 }}
                  />
                  <button style={iconBtn} onClick={() => removeProperty(k)}>
                    ×
                  </button>
                </div>
              ))}
              <button style={addBtn} onClick={addProperty}>
                + Add property
              </button>
            </div>
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 440 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
              <SectionTitle>PINS</SectionTitle>
              <div style={{ display: 'flex', gap: 6 }}>
                <button style={addBtn} onClick={addPin}>
                  + Add pin
                </button>
                <button style={addBtn} onClick={addGroup} title="Create multiple pins in a named connector/group">
                  + Add connector
                </button>
              </div>
            </div>
            {draft.pins.length === 0 && (
              <div style={{ color: '#64748b', fontSize: 12 }}>No pins yet. Click "+ Add pin".</div>
            )}
            <ConnectorGroupedPins
              pins={draft.pins}
              onUpdate={updatePin}
              onRemove={removePin}
              onMove={movePin}
              onRenameGroup={(oldName, newName) => {
                const trimmed = newName.trim();
                const nextPins = draft.pins.map((p) =>
                  (p.group ?? '') === oldName
                    ? { ...p, group: trimmed || undefined }
                    : p,
                );
                update({ pins: nextPins });
              }}
            />
          </div>
        </div>

        <div style={footerStyle}>
          <button style={btnSecondary} onClick={onCancel}>
            Cancel
          </button>
          <button
            style={btnPrimary}
            onClick={() => onSave(draft)}
            disabled={!draft.label.trim()}
          >
            Save to library
          </button>
        </div>
        {connectorDraft && (
          <div style={nestedOverlayStyle} onClick={() => setConnectorDraft(null)}>
            <div style={connectorModalStyle} onClick={(e) => e.stopPropagation()}>
              <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 10 }}>Add connector</div>
              <Field label="Connector / group name">
                <input
                  autoFocus
                  value={connectorDraft.name}
                  onChange={(e) => setConnectorDraft({ ...connectorDraft, name: e.target.value })}
                  placeholder="e.g. MOTOR, USB, DB25 I/O"
                  style={input}
                />
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <Field label="Pin count">
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={connectorDraft.count}
                    onChange={(e) =>
                      setConnectorDraft({ ...connectorDraft, count: Number(e.target.value) || 1 })
                    }
                    style={input}
                  />
                </Field>
                <Field label="Side">
                  <select
                    value={connectorDraft.side}
                    onChange={(e) =>
                      setConnectorDraft({ ...connectorDraft, side: e.target.value as PinSide })
                    }
                    style={input}
                  >
                    {PIN_SIDES.map((side) => (
                      <option key={side} value={side}>
                        {side}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <Field label="Default pin kind">
                <select
                  value={connectorDraft.kind}
                  onChange={(e) =>
                    setConnectorDraft({ ...connectorDraft, kind: e.target.value as PinKind })
                  }
                  style={input}
                >
                  {PIN_KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {kind}
                    </option>
                  ))}
                </select>
              </Field>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                <button style={btnSecondary} onClick={() => setConnectorDraft(null)}>
                  Cancel
                </button>
                <button style={btnPrimary} onClick={commitConnector}>
                  Add connector
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ConnectorGroupedPins({
  pins,
  onUpdate,
  onRemove,
  onMove,
  onRenameGroup,
}: {
  pins: Omit<Pin, 'id'>[];
  onUpdate: (index: number, patch: Partial<Omit<Pin, 'id'>>) => void;
  onRemove: (index: number) => void;
  onMove: (index: number, dir: -1 | 1) => void;
  onRenameGroup: (oldName: string, newName: string) => void;
}) {
  // Build groups while preserving original pin array indices for callbacks.
  const groupMap = new Map<string, { name: string; items: Array<{ pin: Omit<Pin, 'id'>; index: number }> }>();
  pins.forEach((pin, index) => {
    const g = (pin.group ?? '').trim();
    if (!groupMap.has(g)) groupMap.set(g, { name: g, items: [] });
    groupMap.get(g)!.items.push({ pin, index });
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {Array.from(groupMap.values()).map((group) => (
        <ConnectorGroupBlock
          key={group.name || '__ungrouped__'}
          name={group.name}
          items={group.items}
          totalPins={pins.length}
          onUpdate={onUpdate}
          onRemove={onRemove}
          onMove={onMove}
          onRenameGroup={onRenameGroup}
        />
      ))}
    </div>
  );
}

function ConnectorGroupBlock({
  name,
  items,
  totalPins,
  onUpdate,
  onRemove,
  onMove,
  onRenameGroup,
}: {
  name: string;
  items: Array<{ pin: Omit<Pin, 'id'>; index: number }>;
  totalPins: number;
  onUpdate: (index: number, patch: Partial<Omit<Pin, 'id'>>) => void;
  onRemove: (index: number) => void;
  onMove: (index: number, dir: -1 | 1) => void;
  onRenameGroup: (oldName: string, newName: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const isUngrouped = !name;

  return (
    <div
      style={{
        border: '1px solid var(--panel-border)',
        borderRadius: 'var(--radius)',
        background: expanded ? 'var(--panel-alt)' : 'var(--panel)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 6,
          alignItems: 'center',
          padding: 4,
        }}
      >
        <button
          onClick={() => setExpanded((v) => !v)}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-muted)',
            fontSize: 12,
            fontWeight: 700,
            width: 20,
            height: 24,
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? '▾' : '▸'}
        </button>
        {isUngrouped ? (
          <div
            style={{
              flex: 1,
              fontSize: 11,
              color: 'var(--text-muted)',
              fontStyle: 'italic',
              padding: '0 4px',
            }}
          >
            Ungrouped
          </div>
        ) : (
          <input
            value={name}
            onChange={(e) => onRenameGroup(name, e.target.value)}
            style={{
              ...input,
              flex: 1,
              fontWeight: 600,
            }}
          />
        )}
        <div
          onClick={() => setExpanded((v) => !v)}
          style={{
            fontSize: 10,
            color: 'var(--text-dim)',
            whiteSpace: 'nowrap',
            padding: '0 6px 0 2px',
            cursor: 'pointer',
          }}
        >
          {items.length} pin{items.length === 1 ? '' : 's'}
        </div>
      </div>
      {expanded && (
        <div
          style={{
            padding: '2px 8px 8px 26px',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            borderTop: '1px solid var(--panel-border)',
          }}
        >
          {items.map(({ pin, index }) => (
            <div key={index} style={pinRowStyle}>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <span style={{ color: '#64748b', fontSize: 11, width: 22 }}>#{index + 1}</span>
                <button style={iconBtn} onClick={() => onMove(index, -1)} disabled={index === 0}>
                  ↑
                </button>
                <button
                  style={iconBtn}
                  onClick={() => onMove(index, 1)}
                  disabled={index === totalPins - 1}
                >
                  ↓
                </button>
                <input
                  placeholder="Name"
                  value={pin.name}
                  onChange={(e) => onUpdate(index, { name: e.target.value })}
                  style={{ ...input, flex: 1 }}
                />
                <select
                  value={pin.side}
                  onChange={(e) => onUpdate(index, { side: e.target.value as PinSide })}
                  style={{ ...input, width: 80 }}
                >
                  {PIN_SIDES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <select
                  value={pin.kind}
                  onChange={(e) => onUpdate(index, { kind: e.target.value as PinKind })}
                  style={{ ...input, width: 90 }}
                >
                  {PIN_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
                <button style={iconBtnDanger} onClick={() => onRemove(index)}>
                  ×
                </button>
              </div>
              <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                <input
                  placeholder="Group / connector (opt.)"
                  value={pin.group ?? ''}
                  onChange={(e) => onUpdate(index, { group: e.target.value })}
                  style={{ ...input, flex: 1 }}
                />
                <input
                  placeholder="Voltage (opt.)"
                  value={pin.voltage ?? ''}
                  onChange={(e) => onUpdate(index, { voltage: e.target.value })}
                  style={{ ...input, flex: 1 }}
                />
                <input
                  placeholder="Notes (opt.)"
                  value={pin.notes ?? ''}
                  onChange={(e) => onUpdate(index, { notes: e.target.value })}
                  style={{ ...input, flex: 2 }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionTitle({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.8,
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, fontWeight: 500 }}>{label}</div>
      {children}
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.35)',
  backdropFilter: 'blur(2px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  background: 'var(--panel)',
  color: 'var(--text)',
  borderRadius: 'var(--radius)',
  border: '1px solid var(--panel-border)',
  width: 840,
  maxWidth: '95vw',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: 'var(--shadow-lg)',
};

const headerStyle: React.CSSProperties = {
  padding: '14px 18px',
  borderBottom: '1px solid var(--panel-border)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const footerStyle: React.CSSProperties = {
  padding: '12px 18px',
  borderTop: '1px solid var(--panel-border)',
  display: 'flex',
  gap: 8,
  justifyContent: 'flex-end',
  background: 'var(--panel-alt)',
  borderBottomLeftRadius: 12,
  borderBottomRightRadius: 12,
};

const input: React.CSSProperties = {
  background: 'var(--panel)',
  color: 'var(--text)',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 8px',
  fontSize: 12,
  width: '100%',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s, box-shadow 0.15s',
};

const addBtn: React.CSSProperties = {
  padding: '5px 10px',
  background: 'var(--panel)',
  color: 'var(--accent)',
  border: '1px dashed var(--panel-border-strong)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 500,
};

const iconBtn: React.CSSProperties = {
  background: 'var(--panel)',
  color: 'var(--text)',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  width: 26,
  height: 26,
  fontSize: 12,
  padding: 0,
};

const iconBtnDanger: React.CSSProperties = {
  ...iconBtn,
  background: 'var(--danger-soft)',
  color: 'var(--danger)',
  border: '1px solid #fecaca',
};

const btnPrimary: React.CSSProperties = {
  background: 'var(--accent)',
  color: 'white',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  padding: '7px 16px',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  boxShadow: '0 1px 2px rgba(37, 99, 235, 0.25)',
};

const btnSecondary: React.CSSProperties = {
  background: 'var(--panel)',
  color: 'var(--text)',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '7px 16px',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 500,
};

const pinRowStyle: React.CSSProperties = {
  background: 'var(--panel-alt)',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius)',
  padding: 10,
};

const nestedOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1001,
};

const connectorModalStyle: React.CSSProperties = {
  width: 360,
  maxWidth: 'calc(100vw - 48px)',
  background: 'var(--panel)',
  color: 'var(--text)',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius)',
  boxShadow: 'var(--shadow-lg)',
  padding: 14,
};
